/**
 * 画面のヘルプ文書の登録簿。**文章は各アプリが Markdown で持つ。**
 *
 * 文書は `content/<セクション>/<スラッグ>.md` に置き、ディレクトリ名が
 * セクション、`content/` から下のパスがそのまま URL のスラッグになる。
 * 先頭のフロントマターに書くのは見出しと並び順だけである。
 *
 * ## 集めるのはアプリ側、並べるのはここ
 *
 * `import.meta.glob` はそれを書いたモジュールの位置から相対で解決される。
 * ここで glob すると、どのアプリの文書も集められない。だから各アプリが
 * 自分の `content/` を glob して結果を渡し、この関数が slug・セクション・
 * 並び順を決める。登録の表を別に持たないので、ファイルを置けば載る。
 *
 * ## 文章を API から取りに行かない
 *
 * ヘルプは画面の使い方の説明で、会社ごとに変わらない。ビルドに同梱すれば、
 * 「説明が無い」のか「取れなかった」のかを画面が言い分けずに済む。
 */

export interface HelpDoc {
  /** URL のスラッグ（例: "audit/log" → `/help/audit/log`） */
  readonly slug: string;
  readonly section: string;
  readonly title: string;
  readonly order: number;
  /** フロントマターを除いた Markdown の本文 */
  readonly body: string;
}

export interface HelpSection {
  readonly key: string;
  readonly title: string;
  readonly docs: readonly HelpDoc[];
}

/** セクション（`content/` 直下のディレクトリ名）の表示名 */
export interface HelpSectionTitle {
  readonly key: string;
  readonly title: string;
}

export interface HelpRegistry {
  readonly docs: readonly HelpDoc[];
  /** `sectionTitles` の順。表に無いディレクトリは末尾に、ディレクトリ名のまま */
  readonly sections: readonly HelpSection[];
  findHelpDoc(slug: string | undefined): HelpDoc | null;
}

/** 並び順を書き忘れたものは末尾へ。0 と書き忘れを混ぜない */
const DEFAULT_ORDER = 99;

/**
 * フロントマター（`---` で挟んだ `title:` / `order:`）を解く。
 *
 * `order: 0` を書き忘れと同じ扱いにしない（数として読めたかで見分ける）。
 */
export function parseFrontmatter(raw: string): {
  title: string;
  order: number;
  body: string;
} {
  const matched = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/u.exec(raw);
  if (matched === null) {
    return { title: "", order: DEFAULT_ORDER, body: raw };
  }
  let title = "";
  let order = DEFAULT_ORDER;
  for (const line of (matched[1] ?? "").split(/\r?\n/u)) {
    const pair = /^(\w+):\s*(.*)$/u.exec(line);
    if (pair === null) continue;
    const value = (pair[2] ?? "").trim();
    if (pair[1] === "title") title = value;
    if (pair[1] === "order" && value !== "" && Number.isFinite(Number(value))) {
      order = Number(value);
    }
  }
  return { title, order, body: raw.slice(matched[0].length) };
}

/**
 * glob のキーからスラッグを取る。キーの先頭が `./content/` でも
 * `/src/help/content/` でもよいように、最後の `content/` から後ろを使う。
 */
function slugOf(path: string): string {
  const marker = "content/";
  const at = path.lastIndexOf(`/${marker}`);
  const rest =
    at >= 0
      ? path.slice(at + marker.length + 1)
      : path.startsWith(marker)
        ? path.slice(marker.length)
        : path;
  return rest.replace(/\.md$/u, "");
}

function byOrderThenTitle(a: HelpDoc, b: HelpDoc): number {
  return a.order - b.order || a.title.localeCompare(b.title, "ja");
}

/**
 * 文書の集まりから、一覧・セクション・引き当てを作る。
 *
 * @param input.files `import.meta.glob(..., { query: "?raw", import: "default", eager: true })` の結果
 * @param input.sectionTitles セクションの表示名と並び。**ここに無いディレクトリも落とさない**
 *   （落とすと、置いたのに誰からも見えない文書ができる）
 */
export function createHelpRegistry(input: {
  files: Record<string, string>;
  sectionTitles: readonly HelpSectionTitle[];
}): HelpRegistry {
  const docs: HelpDoc[] = [];
  for (const [path, raw] of Object.entries(input.files)) {
    const slug = slugOf(path);
    const section = slug.split("/")[0] ?? slug;
    const { title, order, body } = parseFrontmatter(raw);
    docs.push({
      slug,
      section,
      title: title === "" ? slug : title,
      order,
      body,
    });
  }
  const frozenDocs: readonly HelpDoc[] = Object.freeze(docs);

  const bySection = new Map<string, HelpDoc[]>();
  for (const doc of frozenDocs) {
    const list = bySection.get(doc.section) ?? [];
    list.push(doc);
    bySection.set(doc.section, list);
  }
  const sections: HelpSection[] = [];
  const known = new Set(input.sectionTitles.map((entry) => entry.key));
  for (const entry of input.sectionTitles) {
    const list = bySection.get(entry.key);
    if (list === undefined) continue;
    sections.push({
      key: entry.key,
      title: entry.title,
      docs: [...list].sort(byOrderThenTitle),
    });
  }
  for (const [key, list] of bySection) {
    if (known.has(key)) continue;
    sections.push({ key, title: key, docs: [...list].sort(byOrderThenTitle) });
  }

  return {
    docs: frozenDocs,
    sections: Object.freeze(sections),
    findHelpDoc(slug) {
      if (slug === undefined || slug === "") return null;
      return frozenDocs.find((doc) => doc.slug === slug) ?? null;
    },
  };
}
